import React, { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import LockIcon from "@mui/icons-material/Lock";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import VerifiedIcon from "@mui/icons-material/Verified";
import { ApprovalStatus } from "../../Enums/enums";
import { ApprovalBadge, TimeAgo, WcaBadge, metaFor } from "./constants";

const HEAD_SX = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.7,
  textTransform: "uppercase",
  color: "#64748b",
  bgcolor: "#f8fafc",
  borderBottom: "1px solid #e6ebf1",
  py: 1.1,
  whiteSpace: "nowrap",
};

// Columns that carry the least on a narrow screen. Rather than let seven
// columns squeeze (or force a long sideways scroll for every row), the
// thumbnail goes below sm and brand/created below md — the information they
// held is still on the card view and in the row's own tooltips.
const THUMB_COL_SX = { display: { xs: "none", sm: "table-cell" } };
const WIDE_COL_SX = { display: { xs: "none", md: "table-cell" } };

// Up to six icon buttons live here. On a wide screen they stay on one line;
// below md they wrap into a compact block rather than stretching the row.
const ACTION_CELL_SX = {
  whiteSpace: { xs: "normal", md: "nowrap" },
  width: { xs: 104, md: "auto" },
  "& .MuiIconButton-root": { p: { xs: 0.85, md: 0.6 } },
  "& > *, & > span": { verticalAlign: "middle" },
};

/** Dense alternative to the card grid — for working through a long backlog. */
export default function CompilationsTable({
  rows, isAdmin, onPreview, onManageWgids, onEdit, onDelete, onApprove, onReject,
}) {
  const [approvingId, setApprovingId] = useState(null);

  const handleApprove = async (comp) => {
    setApprovingId(comp.id);
    try {
      await onApprove(comp);
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <TableContainer
      // The container is the only thing allowed to scroll sideways: minWidth 0
      // stops it widening a flex parent, and overflowX keeps a wide row inside
      // this box instead of pushing the whole page.
      sx={{
        border: "1px solid #e6ebf1",
        borderRadius: 3,
        overflowX: "auto",
        overflowY: "hidden",
        minWidth: 0,
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
        bgcolor: "#fff",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: { xs: 0, md: 700 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...HEAD_SX, ...THUMB_COL_SX, width: 88 }} />
            <TableCell sx={HEAD_SX}>Compilation</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...WIDE_COL_SX }}>Brand</TableCell>
            <TableCell sx={HEAD_SX}>WGIDs</TableCell>
            <TableCell sx={{ ...HEAD_SX, ...WIDE_COL_SX }}>Created</TableCell>
            <TableCell sx={HEAD_SX}>Status</TableCell>
            <TableCell sx={{ ...HEAD_SX, textAlign: "right" }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c) => {
            const meta = metaFor(c.compilation_approval_status);
            const wgids = c.wgids || [];
            const isPending = c.compilation_approval_status === ApprovalStatus.PENDING_APPROVAL;
            const playable = Boolean(c.video_url);
            const busy = approvingId === c.id;

            return (
              <TableRow key={c.id} hover sx={{ "& td": { borderBottom: "1px solid #f1f5f9", py: 1 } }}>
                <TableCell sx={{ ...THUMB_COL_SX, borderLeft: `3px solid ${meta.color}` }}>
                  <Box
                    onClick={playable ? () => onPreview(c) : undefined}
                    sx={{
                      position: "relative", width: 68, height: 42, borderRadius: 1.5, overflow: "hidden",
                      bgcolor: "#0b1120", display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: playable ? "pointer" : "default",
                      "&:hover .cmp-t-play": { opacity: 1 },
                    }}
                  >
                    {playable ? (
                      <video src={c.video_url} muted preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <LockIcon sx={{ fontSize: 15, color: "#334155" }} />
                    )}
                    {playable && (
                      <Box
                        className="cmp-t-play"
                        sx={{
                          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                          bgcolor: "rgba(2,6,23,.45)", opacity: 0, transition: "opacity .15s",
                        }}
                      >
                        <PlayArrowIcon sx={{ fontSize: 18, color: "#fff" }} />
                      </Box>
                    )}
                  </Box>
                </TableCell>

                <TableCell
                  sx={{
                    maxWidth: { xs: 190, sm: 300 },
                    // The status rail moves here once the thumbnail column is
                    // hidden, so a row never loses its colour coding.
                    borderLeft: { xs: `3px solid ${meta.color}`, sm: "none" },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap title={c.title}>
                      {c.title}
                    </Typography>
                    {c.is_staged && (
                      <Tooltip title="Private until approved">
                        <LockIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
                      </Tooltip>
                    )}
                  </Box>
                  <Box mt={0.35}><WcaBadge size="small">{c.unique_key}</WcaBadge></Box>
                </TableCell>

                <TableCell sx={WIDE_COL_SX}>
                  <Typography variant="body2" noWrap>{c.brand || "—"}</Typography>
                </TableCell>

                <TableCell>
                  <Tooltip title={wgids.length ? wgids.map((w) => w.wgid).join(", ") : "None attached"}>
                    <Chip
                      size="small"
                      icon={<VerifiedIcon sx={{ fontSize: 13, color: wgids.length ? "#16a34a !important" : undefined }} />}
                      label={wgids.length}
                      sx={{
                        height: 21, fontSize: 11, fontWeight: 700, cursor: "help",
                        color: wgids.length ? "#15803d" : "#64748b",
                        bgcolor: wgids.length ? "#f0fdf4" : "#f1f5f9",
                        "& .MuiChip-label": { px: 0.7 },
                      }}
                    />
                  </Tooltip>
                </TableCell>

                <TableCell sx={WIDE_COL_SX}>
                  <TimeAgo iso={c.created_date} variant="body2" sx={{ display: "block" }} />
                  {c.username && (
                    <Typography variant="caption" color="text.disabled" noWrap>by {c.username}</Typography>
                  )}
                </TableCell>

                <TableCell>
                  <ApprovalBadge status={c.compilation_approval_status} full={false} />
                  {c.compilation_rejection_reason && (
                    <Tooltip title={c.compilation_rejection_reason}>
                      <Typography
                        variant="caption" color="error.main" display="block" noWrap
                        sx={{ maxWidth: { xs: 110, md: 170 }, cursor: "help" }}
                      >
                        {c.compilation_rejection_reason}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell align="right" sx={{ ...ACTION_CELL_SX }}>
                  <Tooltip title={playable ? "Preview" : "No video to preview"}>
                    <span>
                      <IconButton size="small" disabled={!playable} onClick={() => onPreview(c)}>
                        <PlayArrowIcon fontSize="small" color={playable ? "primary" : "disabled"} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {isAdmin && isPending && (
                    <>
                      <Tooltip title="Approve — moves the video to its public S3 path">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => handleApprove(c)}>
                            {busy
                              ? <CircularProgress size={15} />
                              : <ThumbUpAltIcon fontSize="small" sx={{ color: "#10b981" }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Reject — deletes the uploaded video">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => onReject(c)}>
                            <ThumbDownAltIcon fontSize="small" sx={{ color: "#ef4444" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Manage WGIDs">
                    <IconButton size="small" onClick={() => onManageWgids(c)}>
                      <LinkIcon fontSize="small" sx={{ color: "#64748b" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(c)}>
                      <EditIcon fontSize="small" sx={{ color: "#64748b" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => onDelete(c)}>
                      <DeleteOutlineIcon fontSize="small" sx={{ color: "#dc2626" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
